import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Coins, Cpu, Zap } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";

const PROVIDER_COLORS = { anthropic: "#F97316", openai: "#10B981", gemini: "#00E5FF" };

export default function Cost() {
  const [overview, setOverview] = useState(null);
  const [calls, setCalls] = useState([]);

  useEffect(() => {
    api.get("/cost/overview").then((r) => setOverview(r.data));
    api.get("/model-calls?limit=50").then((r) => setCalls(r.data.calls));
  }, []);

  if (!overview) return <div className="p-8 text-[var(--text-dim)]">Cargando…</div>;

  return (
    <div className="p-8 space-y-6" data-testid="cost-page">
      <div className="border-b border-[var(--border)] pb-5">
        <div className="label-mono mb-2">AI COST CONTROL</div>
        <h1 className="font-display font-black text-4xl tracking-tight">Coste IA</h1>
        <p className="text-sm text-[var(--text-dim)] mt-1">
          Llamadas a modelos, tokens y coste <span className="text-[var(--warn)] font-mono text-xs">(ESTIMADO)</span> por agente.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Stat label="Llamadas totales" value={overview.total_calls} icon={Zap} />
        <Stat label="Coste USD" value={`$${overview.total_cost_usd}`} icon={Coins} tone="accent" />
        <Stat label="Agentes activos" value={overview.per_agent.length} icon={Cpu} />
      </div>

      <div className="border border-[var(--border)] bg-[var(--surface)] p-5" data-testid="cost-chart">
        <div className="label-mono mb-3">COSTE POR AGENTE</div>
        {overview.per_agent.length === 0 ? (
          <div className="text-sm text-[var(--text-dim)] font-mono py-8 text-center">Aún sin llamadas.</div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={overview.per_agent.map((p) => ({ name: p.agent_id.toUpperCase(), cost: p.cost_usd, provider: p.provider }))}>
              <XAxis dataKey="name" tick={{ fill: "#94A3B8", fontFamily: "JetBrains Mono", fontSize: 10 }} />
              <YAxis tick={{ fill: "#94A3B8", fontFamily: "JetBrains Mono", fontSize: 10 }} />
              <Tooltip contentStyle={{ background: "#131722", border: "1px solid #272D3D", borderRadius: 0 }} />
              <Bar dataKey="cost" fill="#00E5FF" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="border border-[var(--border)] bg-[var(--surface)]" data-testid="cost-table">
        <div className="grid grid-cols-[80px_120px_100px_100px_100px_100px] label-mono py-3 px-4 border-b border-[var(--border)] bg-[var(--surface-2)]">
          <div>AGENTE</div>
          <div>PROVIDER</div>
          <div>LLAMADAS</div>
          <div>TOKENS IN</div>
          <div>TOKENS OUT</div>
          <div className="text-right">COSTE USD</div>
        </div>
        {overview.per_agent.map((p) => (
          <div key={`${p.agent_id}-${p.provider}`} className="grid grid-cols-[80px_120px_100px_100px_100px_100px] py-3 px-4 border-b border-[var(--border)] font-mono text-xs">
            <div className="uppercase text-[var(--accent)]">{p.agent_id}</div>
            <div style={{ color: PROVIDER_COLORS[p.provider] || "#94A3B8" }}>{p.provider}</div>
            <div>{p.calls}</div>
            <div>{p.tokens_in.toLocaleString("es-ES")}</div>
            <div>{p.tokens_out.toLocaleString("es-ES")}</div>
            <div className="text-right text-white">${p.cost_usd.toFixed(4)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const Stat = ({ label, value, icon: Icon, tone = "default" }) => (
  <div className="border border-[var(--border)] bg-[var(--surface)] p-5">
    <div className="flex items-start justify-between mb-3">
      <div className="label-mono">{label}</div>
      <Icon className="w-4 h-4 text-[var(--text-dim)]" />
    </div>
    <div className={`font-display font-black text-3xl ${tone === "accent" ? "text-[var(--accent)]" : ""}`}>{value}</div>
  </div>
);
